import { ArrowRight, Clock, TrendingUp } from "lucide-react";
import { useRouter } from "next/router";

interface TrialExpiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  daysLeft: number;
}

const PremiumText = () => (
  <span className="font-bold text-purple-600 dark:text-purple-400">
    Premium
  </span>
);

const TrialExpiryModal = ({
  isOpen,
  onClose,
  daysLeft,
}: TrialExpiryModalProps) => {
  const router = useRouter();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-sm bg-black/40 p-4">
      <div
        className="bg-white dark:bg-[#1E1E2D] rounded-xl shadow-2xl w-full max-w-lg animate-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 relative">
          {/* Icon */}
          <div className="relative flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-red-500 rounded-full blur-xl opacity-50 animate-pulse" />
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center shadow-2xl">
                <Clock className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>

          {/* Heading */}
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 dark:from-white dark:via-gray-100 dark:to-white bg-clip-text text-transparent">
              Your Free Plan Is Ending Soon
            </h3>
            <div className="w-16 h-1 bg-gradient-to-r from-orange-400 via-red-500 to-orange-400 mx-auto rounded-full mb-4" />
          </div>

          {/* Message */}
          <div className="mb-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-red-50 to-orange-100 dark:from-orange-400/30 dark:via-red-400/30 dark:to-orange-400/20 rounded-xl" />
            <div className="absolute inset-0 bg-white/40 dark:bg-black/20 backdrop-blur-sm rounded-xl" />

            <div className="relative p-6 border-2 border-orange-200/50 dark:border-orange-700/50 rounded-xl text-center">
              <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-md mx-auto pt-2">
                Your free plan will expire in{" "}
                <span className="font-bold text-red-500">
                  {daysLeft} {daysLeft === 1 ? "day" : "days"}
                </span>
                . Upgrade to a paid plan to continue using <PremiumText />{" "}
                features.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-4">
            <button
              onClick={() => {
                onClose();
                void router.push("/billing");
              }}
              className="group flex-1 relative overflow-hidden px-3 py-2 bg-gradient-to-r from-orange-500 to-red-600 text-white font-semibold rounded-xl shadow-2xl transition-all hover:scale-[1.02] cursor-pointer"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <div className="relative z-10 flex items-center justify-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Upgrade Now
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            <button
              onClick={onClose}
              className="px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrialExpiryModal;
